import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Flower2, Stethoscope, ShieldAlert, CigaretteOff, FlaskConical, Haze, ArrowLeft, ArrowRight, MailCheck } from "lucide-react";
import RevealLine from "@/components/RevealLine";
import Reveal from "@/components/Reveal";
import { api, apiError } from "@/lib/api";
import { useLang } from "@/i18n";

const NEEDS = [
  { key: "allergie", icon: Flower2, l: { it: "Allergie e pollini", en: "Allergies and pollen" } },
  { key: "asma", icon: Stethoscope, l: { it: "Asma e vie respiratorie", en: "Asthma and airways" } },
  { key: "virus_batteri", icon: ShieldAlert, l: { it: "Virus e batteri", en: "Viruses and bacteria" } },
  { key: "odori", icon: CigaretteOff, l: { it: "Cattivi odori e fumo", en: "Bad odours and smoke" } },
  { key: "gas_chimici", icon: FlaskConical, l: { it: "Gas e prodotti chimici (VOC)", en: "Gases and chemicals (VOC)" } },
  { key: "polveri", icon: Haze, l: { it: "Polveri sottili PM2.5", en: "Fine dust PM2.5" } },
];

const ENVIRONMENTS = [
  { it: "Abitazione", en: "Home" },
  { it: "Ufficio", en: "Office" },
  { it: "Studio medico / clinica", en: "Medical practice / clinic" },
  { it: "Scuola", en: "School" },
  { it: "Palestra", en: "Gym" },
  { it: "Hotel / ricettivo", en: "Hotel / hospitality" },
  { it: "Produzione / industria", en: "Production / industry" },
  { it: "Altro", en: "Other" },
];

export default function Quote() {
  const { lang, tr } = useLang();
  const [step, setStep] = useState(0);
  const [needs, setNeeds] = useState([]);
  const [env, setEnv] = useState(ENVIRONMENTS[0].it);
  const [area, setArea] = useState("");
  const [height, setHeight] = useState("3");
  const [contact, setContact] = useState({ name: "", email: "", phone: "", company: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const toggleNeed = (k) =>
    setNeeds((n) => (n.includes(k) ? n.filter((x) => x !== k) : [...n, k]));

  const setC = (k) => (e) => setContact({ ...contact, [k]: e.target.value });

  const canNext =
    step === 0 ? needs.length > 0 : step === 1 ? Number(area) > 0 && Number(height) > 1.5 : true;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/configurator/quote", {
        ...contact,
        environment: env,
        area_m2: Number(area),
        height_m: Number(height),
        needs,
        lang,
      });
      setResult(data);
    } catch (err) {
      toast.error(apiError(err) ?? tr({ it: "Errore nell'invio.", en: "Submission error." }));
    } finally {
      setBusy(false);
    }
  };

  const STEP_TITLES = [
    { it: "Di cosa hai bisogno?", en: "What do you need?" },
    { it: "Il tuo ambiente.", en: "Your space." },
    { it: "I tuoi contatti.", en: "Your details." },
  ];

  return (
    <div data-testid="page-quote" className="bg-white text-[#0a0a0a] pt-32 pb-24 min-h-screen">
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12">
        <div className="font-mono text-[10px] tracking-[0.3em] text-[#007A8A] mb-8">
          {tr({ it: "— PREVENTIVO SU MISURA / RISPOSTA ENTRO 2 ORE", en: "— TAILORED QUOTE / REPLY WITHIN 2 HOURS" })}
        </div>
        <h1 className="font-display text-[clamp(2.8rem,8vw,8rem)] leading-[0.86] tracking-tighter max-w-6xl">
          <div><RevealLine delay={0.2}>{tr({ it: "Il purificatore giusto,", en: "The right purifier," })}</RevealLine></div>
          <div>
            <RevealLine delay={0.35} className="font-editorial italic font-normal text-[#3f3f46]">
              {tr({ it: "calcolato su di te.", en: "calculated for you." })}
            </RevealLine>
          </div>
        </h1>
        <Reveal delay={0.5} className="mt-10 max-w-2xl">
          <p className="font-body text-lg text-[#3f3f46] leading-relaxed">
            {tr({
              it: "Rispondi a poche domande: dimensioniamo la soluzione ideale in base alle tue esigenze e al tuo ambiente (almeno 3 ricambi d'aria ogni ora) e ti inviamo il preventivo dettagliato via email entro 2 ore.",
              en: "Answer a few questions: we size the ideal solution for your needs and your space (at least 3 air changes per hour) and email you a detailed quote within 2 hours.",
            })}
          </p>
        </Reveal>

        <div className="mt-16 max-w-4xl">
          {result ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="border border-[#007A8A] p-10 md:p-16 bg-[#f4f4f5]"
              data-testid="quote-result"
            >
              <MailCheck size={36} className="text-[#007A8A] mb-6" />
              <div className="font-mono text-[10px] tracking-[0.3em] text-[#007A8A] mb-4">
                {tr({ it: "● SOLUZIONE CONSIGLIATA", en: "● RECOMMENDED SOLUTION" })}
              </div>
              <h3 className="font-display text-4xl md:text-6xl tracking-tighter mb-4" data-testid="quote-result-model">
                {result.model}
                {result.quantity > 1 && <span className="text-[#007A8A]"> × {result.quantity}</span>}
              </h3>
              <p className="font-body text-[#3f3f46] leading-relaxed max-w-2xl mb-6">{result.specs}</p>
              <div className="grid grid-cols-2 gap-6 max-w-md mb-10">
                <div className="border border-[#e4e4e7] p-5">
                  <div className="font-display text-3xl tracking-tighter">{result.required_airflow} m³/h</div>
                  <div className="font-mono text-[10px] tracking-widest text-[#52525b] mt-1 uppercase">
                    {tr({ it: `Portata richiesta (${result.ach ?? 3} ricambi/h)`, en: `Required airflow (${result.ach ?? 3} ACH)` })}
                  </div>
                </div>
                <div className="border border-[#e4e4e7] p-5">
                  <div className="font-display text-3xl tracking-tighter">{result.coverage_m2} m²</div>
                  <div className="font-mono text-[10px] tracking-widest text-[#52525b] mt-1 uppercase">
                    {tr({ it: "Copertura della soluzione", en: "Solution coverage" })}
                  </div>
                </div>
              </div>
              <p className="font-body text-[#0a0a0a] text-lg mb-10" data-testid="quote-result-email-note">
                {tr({
                  it: "Il preventivo dettagliato con prezzo e specifiche è in arrivo nella tua casella email entro 2 ore.",
                  en: "The detailed quote with price and specifications will reach your inbox within 2 hours.",
                })}
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/prova-a-casa" className="btn-primary" data-testid="quote-result-trial-cta">
                  {tr({ it: "Prenota la prova a casa", en: "Book a home trial" })} →
                </Link>
                <Link to="/prodotti" className="btn-ghost">
                  {tr({ it: "Vedi la gamma", en: "See the range" })}
                </Link>
              </div>
            </motion.div>
          ) : (
            <div className="border border-[#e4e4e7] bg-[#f4f4f5]">
              {/* Progress */}
              <div className="flex border-b border-[#e4e4e7]">
                {STEP_TITLES.map((s, i) => (
                  <div
                    key={i}
                    className={`flex-1 px-4 py-4 font-mono text-[10px] tracking-[0.2em] uppercase border-r border-[#e4e4e7] last:border-r-0 ${
                      i === step ? "text-[#007A8A] bg-[#fafafa]" : i < step ? "text-[#0a0a0a]" : "text-[#52525b]"
                    }`}
                  >
                    0{i + 1} — {tr(s)}
                  </div>
                ))}
              </div>

              <div className="p-8 md:p-12">
                <AnimatePresence mode="wait">
                  {step === 0 && (
                    <motion.div key="s0" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                      <h3 className="font-display text-3xl md:text-4xl tracking-tighter mb-8">{tr(STEP_TITLES[0])}</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {NEEDS.map((n) => {
                          const Icon = n.icon;
                          const on = needs.includes(n.key);
                          return (
                            <button
                              key={n.key}
                              type="button"
                              onClick={() => toggleNeed(n.key)}
                              data-testid={`need-${n.key}`}
                              className={`text-left border p-5 transition-colors ${
                                on ? "border-[#007A8A] bg-[#fafafa]" : "border-[#e4e4e7] hover:border-[#3a3d46]"
                              }`}
                            >
                              <Icon size={20} className={on ? "text-[#007A8A]" : "text-[#3f3f46]"} />
                              <div className="font-body text-sm text-[#0a0a0a] mt-3">{tr(n.l)}</div>
                            </button>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}

                  {step === 1 && (
                    <motion.div key="s1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                      <h3 className="font-display text-3xl md:text-4xl tracking-tighter mb-8">{tr(STEP_TITLES[1])}</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <Field label={tr({ it: "Tipo di ambiente", en: "Type of space" })} required>
                          <select value={env} onChange={(e) => setEnv(e.target.value)} data-testid="quote-environment" className="input-aeras">
                            {ENVIRONMENTS.map((o) => (
                              <option key={o.it} value={o.it} className="bg-[#f4f4f5]">{tr(o)}</option>
                            ))}
                          </select>
                        </Field>
                        <Field label={tr({ it: "Superficie (m²)", en: "Floor area (m²)" })} required>
                          <input type="number" min="1" step="1" value={area} onChange={(e) => setArea(e.target.value)} data-testid="quote-area" className="input-aeras" placeholder="es. 120" />
                        </Field>
                        <Field label={tr({ it: "Altezza soffitto (m)", en: "Ceiling height (m)" })} required>
                          <input type="number" min="2" max="20" step="0.1" value={height} onChange={(e) => setHeight(e.target.value)} data-testid="quote-height" className="input-aeras" />
                        </Field>
                      </div>
                      <p className="font-mono text-[10px] tracking-widest uppercase text-[#52525b] mt-8 max-w-lg">
                        {tr({
                          it: "Dimensioniamo la soluzione per garantire almeno 3 ricambi d'aria completi ogni ora (5 ricambi/ora in caso di asma o vie respiratorie sensibili).",
                          en: "We size the solution to deliver at least 3 complete air changes per hour (5 per hour for asthma or sensitive airways).",
                        })}
                      </p>
                    </motion.div>
                  )}

                  {step === 2 && (
                    <motion.form key="s2" onSubmit={submit} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                      <h3 className="font-display text-3xl md:text-4xl tracking-tighter mb-8">{tr(STEP_TITLES[2])}</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Field label={tr({ it: "Nome e cognome", en: "Full name" })} required>
                          <input required value={contact.name} onChange={setC("name")} data-testid="quote-name" className="input-aeras" />
                        </Field>
                        <Field label="Email" required>
                          <input required type="email" value={contact.email} onChange={setC("email")} data-testid="quote-email" className="input-aeras" />
                        </Field>
                        <Field label={tr({ it: "Telefono", en: "Phone" })}>
                          <input value={contact.phone} onChange={setC("phone")} data-testid="quote-phone" className="input-aeras" />
                        </Field>
                        <Field label={tr({ it: "Azienda", en: "Company" })}>
                          <input value={contact.company} onChange={setC("company")} data-testid="quote-company" className="input-aeras" />
                        </Field>
                      </div>
                      <div className="mt-6">
                        <Field label={tr({ it: "Note (opzionale)", en: "Notes (optional)" })}>
                          <textarea rows={3} value={contact.notes} onChange={setC("notes")} data-testid="quote-notes" className="input-aeras resize-none" />
                        </Field>
                      </div>
                      <div className="flex items-center justify-between mt-10 flex-wrap gap-4">
                        <button type="button" onClick={() => setStep(1)} className="btn-ghost" data-testid="quote-back-2">
                          <ArrowLeft size={14} /> {tr({ it: "Indietro", en: "Back" })}
                        </button>
                        <button type="submit" disabled={busy} className="btn-primary disabled:opacity-50" data-testid="quote-submit">
                          {busy
                            ? tr({ it: "Calcolo in corso…", en: "Calculating…" })
                            : tr({ it: "Ricevi il preventivo via email →", en: "Get your quote by email →" })}
                        </button>
                      </div>
                      <p className="font-mono text-[10px] tracking-widest uppercase text-[#52525b] mt-6">
                        {tr({ it: "Dati trattati ai sensi del GDPR 2016/679.", en: "Data processed under GDPR 2016/679." })}
                      </p>
                    </motion.form>
                  )}
                </AnimatePresence>

                {step < 2 && (
                  <div className="flex items-center justify-between mt-10">
                    <button
                      type="button"
                      onClick={() => setStep((s) => Math.max(0, s - 1))}
                      className={`btn-ghost ${step === 0 ? "invisible" : ""}`}
                      data-testid="quote-back"
                    >
                      <ArrowLeft size={14} /> {tr({ it: "Indietro", en: "Back" })}
                    </button>
                    <button
                      type="button"
                      disabled={!canNext}
                      onClick={() => setStep((s) => s + 1)}
                      className="btn-primary disabled:opacity-40"
                      data-testid="quote-next"
                    >
                      {tr({ it: "Avanti", en: "Next" })} <ArrowRight size={14} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      <style>{`
        .input-aeras {
          width: 100%; background: transparent; border: none;
          border-bottom: 1px solid #d4d4d8; padding: 0.85rem 0; color: #0a0a0a;
          font-family: 'IBM Plex Sans', sans-serif; font-size: 1rem; outline: none;
          transition: border-color 0.3s ease; color-scheme: light;
        }
        .input-aeras:focus { border-bottom-color: #007A8A; }
        .input-aeras::placeholder { color: #71717a; }
      `}</style>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#3f3f46] mb-2 inline-block">
        {label}
        {required && <span className="text-[#FF4D00] ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}
